import { EventEmitter, Inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { Observable } from 'rxjs';

import { ClientTranslationKeyModel } from '../models/language-locale.model';

@Injectable({
	providedIn: 'root'
})
export class TranslationManagerService {
	public clientTranslationKeys: ClientTranslationKeyModel[] = null;

	/**
	 * TODO - Refactor this stuff into its own service, should just be able to execute the same observable straight from the service.
	 * Alos preferably we will constantly update this with signal r and do cool stuff
	 * Emits an event anytime a request is performed that requires translation data to be updated. Currently needs to happen in translationManager base to avoid code replication
	 */
	public translationDataModified: EventEmitter<ClientTranslationKeyModel> =
		new EventEmitter<ClientTranslationKeyModel>();

	constructor(
		private httpClient: HttpClient,
		@Inject('environment') public environment: any
	) {}

	/**
	 * Gets all i18n locales from the database.
	 */
	public getClientTranslationKeys(): Observable<ClientTranslationKeyModel[]> {
		return this.httpClient.get<ClientTranslationKeyModel[]>(
			this.environment.apiUrl + `api/Contract/GetAllClientLanguageRecords`
		);
	}

	/**
	 * Merges the current user edits into LANG.ClientLanguage.
	 * After a succesful merge, database translation store is refreshed entirely. NOTE - We could just index current records and update. Not sure how I want to handle this. Overwrite prevention will likely change how this is done
	 * TODO - If you change the language this will just create a new record instead of modifying the existing one. Also make error handling work.
	 * @param userLanguageEdits The current user translation edits
	 * @returns Promise<ClientTranslationKeyModel>
	 */
	public async mergeClientTranslationKeysToDb(
		userLanguageEdits: ClientTranslationKeyModel
	): Promise<ClientTranslationKeyModel> {
		const mergedKey = await this.httpClient
			.post<ClientTranslationKeyModel>(
				this.environment.apiUrl + `api/Contract/MergeUserLanguageEditsToDb?userEditedKey`,
				userLanguageEdits
			)
			.toPromise();
		this.translationDataModified.emit(mergedKey);

		return mergedKey;
	}

	/**
	 * Looks at the database for any LANG.ClientLanguage unique key groupings that have an english key, but are missing records for one or more configured languages.
	 * If an english key is present, a record is created for each configured language in the environment. This new record is populated with google translated text.
	 * @note This will re-translate and OVERWRITE all existing translated text for the unique keys translated.
	 * @returns List of keys that were translated and merged into the DB
	 */
	public createAndReTranslateAllIncompleteKeys(): Promise<JSON> {
		return new Promise((resolve, reject) => {
			this.httpClient
				.post<any>(this.environment.apiUrl + `api/Contract/CreateAndReTranslateAllIncompleteKeys`, null)
				.toPromise()
				.then(
					(response) => {
						this.translationDataModified.emit(null);
						resolve(response);
					},
					(error) => {
						reject(new HttpErrorResponse(error));
					}
				);
		});
	}

	/**
	 * Given the provided key, creates traslation locales for any missing languages specifed for the current contract.
	 * Overwrites and re-translates all existing translation values.
	 * @note Provided translation key must contain a english locale, as this is the base locale we translate from.
	 * @param requestedTranslationKey
	 * @returns List of translated locales for provided key
	 */
	public createAndReTranslateProvidedEnglishKeys(requestedTranslationKey: ClientTranslationKeyModel): Promise<JSON> {
		return new Promise((resolve, reject) => {
			this.httpClient
				.post<any>(
					this.environment.apiUrl + `api/Contract/CreateAndReTranslateProvidedEnglishKeys`,
					Array(requestedTranslationKey)
				)
				.toPromise()
				.then(
					(response) => {
						this.translationDataModified.emit(null);
						resolve(response);
					},
					(error) => {
						reject(new HttpErrorResponse(error));
					}
				);
		});
	}

	/**
	 * Updates the IsActive flag for a portal page translation key mapping
	 * @param pageNameClientLanguageId The ID of the PageNameClientLanguage record
	 * @param isActive The new IsActive value
	 * @returns Promise<boolean>
	 */
	public async updatePortalPageIsActive(pageNameClientLanguageId: number, isActive: boolean): Promise<boolean> {
		const result = await this.httpClient
			.post<boolean>(this.environment.apiUrl + `api/Contract/UpdatePortalPageIsActive`, {
				pageNameClientLanguageId,
				isActive
			})
			.toPromise();
		this.translationDataModified.emit(null);
		return result;
	}
}
